// Enterprise-grade color tokens following observability platform standards (Datadog, New Relic, Grafana)
export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
        // Dark Mode Palette - Pure Black / OLED Aesthetic
        grey: {
          50: "#fafafa",
          100: "#f5f5f5",
          200: "#eeeeee",
          300: "#e0e0e0",
          400: "#bdbdbd",
          500: "#9e9e9e",
          600: "#757575",
          700: "#616161",
          800: "#424242",
          900: "#1a1a1a",
        },
        primary: {
          50: "#eff6ff",
          100: "#dbeafe",
          200: "#bfdbfe",
          300: "#93c5fd",
          400: "#60a5fa",
          500: "#3b82f6", // Electric Blue
          600: "#2563eb",
          700: "#1d4ed8",
          800: "#1e40af",
          900: "#1e3a8a",
        },
        background: {
          default: "#000000", // OLED Black
          paper: "#0a0a0a",   // Slightly elevated
          elevated: "#121212",
          overlay: "#18181b",
        },
        surface: {
          default: "#0a0a0a",
          hover: "#121212",
          selected: "#18181b",
          disabled: "#000000",
        },
        border: {
          default: "rgba(255, 255, 255, 0.1)",
          muted: "rgba(255, 255, 255, 0.06)",
          subtle: "rgba(255, 255, 255, 0.03)",
        },
        status: {
          success: "#10b981", // Luminous Emerald
          successBg: "rgba(16, 185, 129, 0.1)",
          warning: "#f59e0b", // Luminous Amber
          warningBg: "rgba(245, 158, 11, 0.1)",
          error: "#ef4444",   // Luminous Red
          errorBg: "rgba(239, 68, 68, 0.1)",
          info: "#3b82f6",
          infoBg: "rgba(59, 130, 246, 0.1)",
        },
        // Semantic colors for metrics
        metrics: {
          healthy: "#10b981",
          degraded: "#f59e0b",
          critical: "#ef4444",
          unknown: "#71717a",
        },
        // Data visualization colors (vivid & distinct for dark mode)
        chart: {
          blue: "#3b82f6",
          green: "#10b981",
          yellow: "#f59e0b",
          red: "#ef4444",
          purple: "#8b5cf6",
          orange: "#f97316",
          teal: "#14b8a6",
          pink: "#ec4899",
          indigo: "#6366f1",
          cyan: "#06b6d4",
        },
      }
    : {
        // Light Mode Palette (Crisp & Clean)
        grey: {
          50: "#fafafa",
          100: "#f4f4f5",
          200: "#e4e4e7",
          300: "#d4d4d8",
          400: "#a1a1aa",
          500: "#71717a",
          600: "#52525b",
          700: "#3f3f46",
          800: "#27272a",
          900: "#18181b",
        },
        primary: {
          50: "#eff6ff",
          100: "#dbeafe",
          200: "#bfdbfe",
          300: "#93c5fd",
          400: "#60a5fa",
          500: "#3b82f6",
          600: "#2563eb",
          700: "#1d4ed8",
          800: "#1e40af",
          900: "#1e3a8a",
        },
        background: {
          default: "#f8fafc",
          paper: "#ffffff",
          elevated: "#ffffff",
          overlay: "#f1f5f9",
        },
        surface: {
          default: "#ffffff",
          hover: "#f8fafc",
          selected: "#f1f5f9",
          disabled: "#fbfcfd",
        },
        border: {
          default: "rgba(0, 0, 0, 0.08)",
          muted: "rgba(0, 0, 0, 0.04)",
          subtle: "rgba(0, 0, 0, 0.02)",
        },
        status: {
          // #059669 on white is 3.4:1 — under 4.5:1 — so outlined success Chips ("HEALTHY")
          // failed. #047857 clears it, and as with `warning` this token doubles as a filled
          // background, so darkening improves white-on-success too.
          success: "#047857",
          successBg: "#d1fae5",
          // WCAG AA. #d97706 on white is 3.18:1 and on #fcfcfc 3.10:1 — under the 4.5:1
          // minimum — which made every outlined warning Chip ("No data", "Medium") fail
          // contrast. #b45309 clears it on both, and because this token is ALSO used as a
          // filled-chip background, darkening it improves white-on-warning at the same time.
          // Found by the axe sweep; fixed at the token so every future use inherits it.
          warning: "#b45309",
          warningBg: "#fef3c7",
          error: "#dc2626",
          errorBg: "#fee2e2",
          // #2563eb on #dbeafe measures 4.23:1 — just under 4.5:1 — so every standard info
          // Alert failed. #1d4ed8 clears it against the same background.
          info: "#1d4ed8",
          infoBg: "#dbeafe",
        },
        // Semantic colors for metrics
        metrics: {
          healthy: "#059669",
          degraded: "#d97706",
          critical: "#dc2626",
          unknown: "#71717a",
        },
        // Data visualization colors (high contrast for light mode)
        chart: {
          blue: "#2563eb",
          green: "#059669",
          yellow: "#d97706",
          red: "#dc2626",
          purple: "#7c3aed",
          orange: "#ea580c",
          teal: "#0d9488",
          pink: "#db2777",
          indigo: "#4f46e5",
          cyan: "#0891b2",
        },
      }),
});

// Shared categorical palette for charts (distinct hues; works on the dark theme).
// Use index-based assignment so every chart across VerOps colors series the same way.
export const CHART_PALETTE = [
  "#3b82f6", // blue
  "#14b8a6", // teal
  "#f59e0b", // amber
  "#a78bfa", // violet
  "#22c55e", // green
  "#ef4444", // red
  "#ec4899", // pink
  "#64748b", // slate
];